import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Logo } from "../lib/brand";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

type State = "working" | "ok" | "error";

export default function Verify() {
  const [state, setState] = useState<State>("working");
  const [message, setMessage] = useState<string>("Confirming your email…");
  const [okTitle, setOkTitle] = useState<string>("Email verified");
  const { setUser, refresh, loading } = useAuth();
  const [, navigate] = useLocation();
  const ran = useRef(false);

  useEffect(() => {
    // Initial account loading remounts profile-scoped pages; wait so this
    // single-use token is not submitted again after a successful confirmation.
    if (loading) return;
    if (ran.current) return;
    ran.current = true;
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token") || "";
    const isChange = params.get("mode") === "email-change";
    if (!token) { setState("error"); setMessage("This link is missing its verification token."); return; }
    (async () => {
      try {
        const r = isChange ? await api.confirmEmailChange(token) : await api.confirmVerifyEmail(token);
        if (r?.ok) {
          setState("ok");
          if (isChange) setOkTitle("Email updated");
          setMessage(isChange
            ? `Your email has been updated${(r as any).email ? ` to ${(r as any).email}` : ""}. You're all set.`
            : "Your email is verified. You're all set.");
          if (r.user) setUser(r.user);
          else await refresh();
        } else {
          setState("error");
          setMessage("We couldn't confirm this link.");
        }
      } catch (e: any) {
        setState("error");
        setMessage(e?.message || "This link is invalid or has expired.");
      }
    })();
  }, [loading, setUser, refresh]);

  return (
    <div className="min-h-[80vh] grid place-items-center container-x py-10">
      <div className="card p-6 sm:p-8 max-w-md w-full text-center">
        <div className="flex justify-center mb-4"><Logo withTagline /></div>
        {state === "working" && <Loader2 className="w-10 h-10 text-mut mx-auto animate-spin" />}
        {state === "ok" && <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />}
        {state === "error" && <XCircle className="w-12 h-12 text-brand-red mx-auto" />}
        <h1 className="text-xl font-extrabold text-navy mt-4">
          {state === "ok" ? okTitle : state === "error" ? "Verification failed" : "One moment…"}
        </h1>
        <p className="text-sm text-mut mt-2">{message}</p>
        <div className="mt-6 flex flex-col gap-2">
          {state === "ok" && <button onClick={() => navigate("/dashboard")} className="btn-red w-full">Go to Dashboard</button>}
          {state === "error" && <Link href="/dashboard" className="btn-red w-full">Back to Dashboard</Link>}
        </div>
      </div>
    </div>
  );
}
